import React, { useRef } from 'react';
import {
    FormGroup,
    Card,
    CardBody,
    Input,
    InputGroup,
    InputGroupAddon,
    UncontrolledButtonDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from 'reactstrap';
import Select from 'react-select';
import FlatPicker from 'react-flatpickr';
import 'flatpickr/dist/themes/dark.css';
import { MdCode, MdOutlineFileDownload, MdPlayArrow } from 'react-icons/md';
import { Icon } from '@iconify/react';
import calendarRange from '@iconify/icons-mdi/calendar-range';
import { ButtonLoader } from '../../../../components/Loader.js';
import MultiSelectDropdown from './MultiSelectDropdown.js';
import { EXPORT_FORMATS, formatDateForApi } from '../pharmacyReportsHelpers.js';

const FLATPICKR_OPTIONS = {
    altInput: true,
    altFormat: 'm/d/Y',
    dateFormat: 'Y-m-d',
    position: 'below',
};

function ParameterLabel({ parameter }) {
    return (
        <label className="pharmacy-reports-field-label">
            {parameter.displayName}
            {parameter.isRequired && <span className="text-danger"> *</span>}
        </label>
    );
}

function DateField({ value, onChange }) {
    const pickerRef = useRef(null);

    return (
        <InputGroup className="input-group-custom">
            <FlatPicker
                ref={pickerRef}
                className="form-control bg-white"
                placeholder="MM/DD/YYYY"
                value={value || ''}
                onChange={(dates) => onChange(dates && dates[0] ? formatDateForApi(dates[0]) : '')}
                options={FLATPICKR_OPTIONS}
            />
            <InputGroupAddon addonType="append">
                <span className="input-group-text" onClick={() => pickerRef.current?.flatpickr?.open()}>
                    <Icon icon={calendarRange} width="18px" />
                </span>
            </InputGroupAddon>
        </InputGroup>
    );
}

function CustomParameterField({ parameter, value, onChange }) {
    if (parameter.dataType === 'Date') {
        return <DateField value={value} onChange={onChange} />;
    }

    if (parameter.dataType === 'List') {
        const options = [...(parameter.options || [])].sort((a, b) =>
            String(a.label ?? '').localeCompare(String(b.label ?? ''))
        );
        const selected = value ? value.split(',') : [];
        const toggleOption = (optionValue) =>
            onChange(
                (selected.includes(optionValue)
                    ? selected.filter((item) => item !== optionValue)
                    : [...selected, optionValue]
                ).join(',')
            );

        return (
            <MultiSelectDropdown
                options={options}
                selectedValues={selected}
                onToggleOption={toggleOption}
                onClear={() => onChange('')}
                placeholder="All"
                allLabel="All"
                onToggleAll={() =>
                    onChange(selected.length === options.length ? '' : options.map((option) => option.value).join(','))
                }
            />
        );
    }

    if (parameter.dataType === 'Boolean') {
        const booleanOptions = [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
        ];
        return (
            <Select
                placeholder="Choose yes or no"
                isClearable
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                value={booleanOptions.find((option) => option.value === value) || null}
                onChange={(option) => onChange(option ? option.value : '')}
                options={booleanOptions}
            />
        );
    }

    const isNumber = parameter.dataType === 'Integer' || parameter.dataType === 'Decimal';
    return (
        <Input
            type={isNumber ? 'number' : 'text'}
            step={parameter.dataType === 'Integer' ? '1' : 'any'}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
        />
    );
}

function ReportFiltersCard({
    reports,
    isLoadingReports,
    selectedReportId,
    onReportChange,
    report,
    pharmacy,
    facility,
    values,
    onValueChange,
    canRun,
    canRunPermission,
    isRunning,
    onRun,
    canExport,
    isExporting,
    onExport,
    canPreviewSql,
    isLoadingSql,
    onPreviewSql,
}) {
    const visibleParameters = (report?.parameters || []).filter((parameter) => parameter.parameterType !== 'User');

    const renderParameter = (parameter) => {
        if (parameter.parameterType === 'Pharmacies') {
            return (
                <MultiSelectDropdown
                    options={pharmacy.options}
                    selectedValues={pharmacy.selected}
                    onToggleOption={pharmacy.handlers.onToggle}
                    onClear={pharmacy.handlers.onClear}
                    placeholder="All pharmacies"
                    allLabel="All Pharmacy"
                    onToggleAll={pharmacy.handlers.onToggleAll}
                />
            );
        }

        if (parameter.parameterType === 'Facilities') {
            return (
                <MultiSelectDropdown
                    options={facility.options}
                    selectedValues={facility.selected}
                    onToggleOption={facility.handlers.onToggle}
                    onClear={facility.handlers.onClear}
                    placeholder="All facilities"
                    allLabel="All Facility"
                    onToggleAll={facility.handlers.onToggleAll}
                />
            );
        }

        return (
            <CustomParameterField
                parameter={parameter}
                value={values[parameter.internalName]}
                onChange={(value) => onValueChange(parameter.internalName, value)}
            />
        );
    };

    return (
        <Card style={{ padding: '20px 0px' }} className="pharmacy-reports-card">
            <CardBody>
                <div className="pharmacy-reports-filters-row">
                    <div className="field-col" style={{ flex: '0 0 150px', minWidth: '350px', maxWidth: '350px' }}>
                        <FormGroup className="mb-0">
                            <label className="pharmacy-reports-field-label">Report</label>
                            <Select
                                placeholder="Select a report..."
                                isLoading={isLoadingReports}
                                isClearable
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                value={
                                    selectedReportId
                                        ? {
                                              value: selectedReportId,
                                              label:
                                                  reports.find(
                                                      (item) => String(item.internalReportId) === selectedReportId
                                                  )?.reportName || '',
                                          }
                                        : null
                                }
                                onChange={(option) => onReportChange(option ? option.value : '')}
                                options={reports.map((item) => ({
                                    value: String(item.internalReportId),
                                    label: item.reportName,
                                }))}
                            />
                        </FormGroup>
                    </div>
                </div>

                {visibleParameters.length > 0 && (
                    <div className="pharmacy-reports-filters-row">
                        {visibleParameters.map((parameter) => (
                            <div className="field-col" key={parameter.internalName}>
                                <FormGroup className="mb-0">
                                    <ParameterLabel parameter={parameter} />
                                    {renderParameter(parameter)}
                                </FormGroup>
                            </div>
                        ))}
                    </div>
                )}

                {canRunPermission && (
                    <div className="pharmacy-reports-actions-row">
                        <button
                            type="button"
                            style={{ gap: '8px' }}
                            className="btn btn-primary pharmacy-reports-btn d-flex align-items-center gap-2 w-fit"
                            disabled={!canRun || isRunning}
                            onClick={onRun}
                        >
                            <MdPlayArrow size={18} />
                            Run Report
                        </button>
                        <UncontrolledButtonDropdown>
                            <DropdownToggle
                                caret
                                color="primary"
                                outline
                                style={{ gap: '8px' }}
                                className="pharmacy-reports-btn d-flex align-items-center w-fit"
                                disabled={!canRun || !canExport || isExporting}
                            >
                                {isExporting ? <ButtonLoader /> : <MdOutlineFileDownload size={18} />}
                                Export
                            </DropdownToggle>
                            <DropdownMenu>
                                {EXPORT_FORMATS.map((option) => (
                                    <DropdownItem key={option.value} onClick={() => onExport(option.value)}>
                                        {option.label}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </UncontrolledButtonDropdown>
                        {canPreviewSql && (
                            <button
                                type="button"
                                style={{ gap: '8px' }}
                                className="btn btn-outline-secondary pharmacy-reports-btn d-flex align-items-center gap-2 w-fit"
                                disabled={!report || isLoadingSql}
                                onClick={onPreviewSql}
                            >
                                <MdCode size={18} />
                                Preview SQL
                            </button>
                        )}
                    </div>
                )}
            </CardBody>
        </Card>
    );
}

export default ReportFiltersCard;
